import { useRef, useCallback } from "react";

type CacheEntry<T> = {
  key: string;
  from: number;
  to: number;
  data: T[];
};

/**
 * Superset cache for chart data.
 * When switching to a smaller time range, slices cached data instantly (no fetch).
 * When switching to a larger range, fetches the full range and updates the cache.
 */
export function useChartCache<T>(getTimestamp: (point: T) => number) {
  const cacheRef = useRef<CacheEntry<T> | null>(null);

  const trySlice = useCallback(
    (key: string, from: number, to: number): T[] | null => {
      const c = cacheRef.current;
      if (!c || c.key !== key) return null;
      if (from >= c.from && to <= c.to) {
        return c.data.filter(
          (p) => getTimestamp(p) >= from && getTimestamp(p) <= to,
        );
      }
      return null;
    },
    [getTimestamp],
  );

  const store = useCallback(
    (key: string, from: number, to: number, data: T[]) => {
      const c = cacheRef.current;
      // Update cache if: no cache yet, different key, larger/equal range, or newer data (to moved forward)
      if (!c || c.key !== key || to - from >= c.to - c.from || to > c.to) {
        cacheRef.current = { key, from, to, data };
      }
    },
    [],
  );

  const clear = useCallback(() => {
    cacheRef.current = null;
  }, []);

  return { trySlice, store, clear };
}
